import { Component, h, Listen, Prop, State, Watch } from '@stencil/core';
import store from '../../helpers/store';
// import { ML5 } from '../../helpers/ML5';
import { FlowStatus } from '../../models/FlowStatus';
import Events from '../../helpers/Events';
import packageJson from '../../../package.json';
import { getLogMessage, getUUID } from '../../utils/utils';
import { Browser } from '../../models/IDevice';
import { FlowMoments, FlowSteps } from '../../models/FlowSteps';

// @ts-ignore
import loaderImg from '../../assets/loader.svg';
import { ApiCall } from '../../helpers/ApiCall';
import { EctData } from '../../helpers/EctDb';
import { db } from '../../helpers/db';
import AppInsightsService from '../../helpers/AppInsightsService';
import { LoggerService } from '../../helpers/LoggerService';
import { modelLoadingService } from '../../helpers/ModelLoadingService';
import { SmartDeviceDetector } from '../../helpers/SensorsInfo';

// @ts-ignore
//import cv from 'opencv.js';

@Component({
  tag: 'identification-component',
  styleUrl: 'identification-component.css',
  shadow: true,
})
export class IdentificationComponent {
  @Prop({ mutable: true, reflect: true }) token: string;
  @Prop({ mutable: true, reflect: true }) order_id: string;
  @Prop({ mutable: true, reflect: true }) api_url: string;
  @Prop({ mutable: true, reflect: true }) env: string;
  @Prop({ mutable: true, reflect: true }) redirect_id: string;
  @Prop({ mutable: true, reflect: true }) phone_number: string;
  @Prop({ mutable: true, reflect: true }) lang_iso: string;
  @Prop({ mutable: true, reflect: true }) citizenship: string;
  @Prop({ mutable: true, reflect: true }) show_citizenship_selector: boolean;
  @Prop({ mutable: true, reflect: true }) scroll_mode: boolean;
  @Prop({ mutable: true, reflect: true }) analytics: boolean;
  @State() private errorMessage: string;
  @State() private errorTitle: string;

  private logInit: any = {};
  private initialised = false;
  private ectData: EctData;
  private appInsights: AppInsightsService;

  @Watch('token')
  async onTokenChange(newValue: string, _oldValue: string) {
    LoggerService.log('Token change called with value: ' + newValue);
    if (newValue == '') {
      newValue = store.token;
      return;
    }

    if (store.token !== newValue) {
      store.token = newValue;
    }
  }

  @Listen('apiError')
  async apiErrorEmitter(data: any, zone: string) {
    if (data.detail) {
      this.errorTitle = data.detail.message ?? data.detail.status ?? '';
      this.errorMessage = data.detail.stack ?? data.detail.statusText ?? '';
    } else if (data.message) {
      this.errorTitle = data.message;
    } else if (data.status) {
      this.errorTitle = data.status ?? '';
      this.errorMessage = data.statusText ?? '';
    } else {
      this.errorTitle = data;
    }
    let apiLogData = { message: this.errorTitle, stack: this.errorMessage, zone };
    try {
      await ApiCall.instance.AddLog(apiLogData, await getLogMessage(this.order_id, this.redirect_id, this.token));
    } catch (e) {
      this.appInsights.trackException({ name: 'IdentificationComponent | error', requestId: store.requestId }, e);
    }

    await db.ectdata.delete(store.requestId);

    Events.flowError(this.errorTitle);
    store.flowStatus = FlowStatus.ERROREND;
  }

  constructor() {
    LoggerService.disable();
  }

  async componentWillLoad() {
    let smartDetect = await SmartDeviceDetector.getInstance().getDetectionResult();
    store.device = smartDetect.userAgentInfo;
    store.sensors = smartDetect.sensorResult;

    store.analytics = this.analytics ?? true;
    this.appInsights = AppInsightsService.getInstance();

    Events.init(window);
    Events.flowEvent(FlowSteps.ComponentLoaded, FlowMoments.Initialized);

    store.apiBaseUrl = this.api_url;
    if (this.env && this.env != '') {
      store.environment = this.env;
    }
    if (store.environment != 'PROD') {
      store.debug = true;
      LoggerService.enable();
    }
    ApiCall.getInstance();

    if (store.debug) this.logInit = await getLogMessage(this.order_id, this.redirect_id, this.token);

    if (this.token) {
      LoggerService.log('Store Token set with property value: ' + this.token);
      store.token = this.token;
    }

    if (this.redirect_id) {
      store.redirectId = this.redirect_id;
    }

    if (this.lang_iso && this.lang_iso != '') {
      store.langIso = this.lang_iso;
    }

    // if (this.citizenship && this.citizenship != '') {
    //   store.citizenship = this.citizenship;
    // }

    store.showCitizenshipSelector = this.show_citizenship_selector ?? false;
    store.scrollMode = this.scroll_mode ?? false;
  }

  async componentDidLoad() {
    if (this.order_id && this.order_id != '') {
      this.ectData = await db.getOrCreateEctData(this.order_id);

      store.requestId = this.order_id;
      this.appInsights.trackEvent('IdentificationComponent | init', { requestId: store.requestId });
      store.initialized = this.ectData.initialized;
      store.idModelsPath = this.ectData.idModelsPath;
      store.faceModelsPath = this.ectData.faceModelsPath;
      LoggerService.log('Current RequestId has value: ' + this.order_id);
    } else if (!this.redirect_id || this.redirect_id == '') {
      var lastEct = await db.getLastEctData(false);
      if (lastEct) {
        this.ectData = lastEct;
        store.requestId = this.ectData.orderId;
        store.initialized = this.ectData.initialized;
      }
    }

    if (this.ectData?.idModelsPath && this.ectData?.idModelsPath != '') {
      store.idModelsPath = this.ectData.idModelsPath;
    }

    if (this.ectData?.faceModelsPath && this.ectData?.faceModelsPath != '') {
      store.faceModelsPath = this.ectData.faceModelsPath;
    }

    if (this.phone_number && this.phone_number != '') {
      store.phoneNumber = this.phone_number;
      this.ectData.phoneNumber = this.phone_number;
      await db.ectdata.put(this.ectData);
    } else if (this.ectData?.phoneNumber && this.ectData?.phoneNumber != '') {
      store.phoneNumber = this.ectData.phoneNumber;
    }

    if (this.citizenship && this.citizenship != '') {
      store.citizenship = this.citizenship;
      this.ectData.citizenship = this.citizenship;
      await db.ectdata.put(this.ectData);
    } else if (this.ectData?.citizenship && this.ectData?.citizenship != '') {
      store.citizenship = this.ectData.citizenship;
    }

    await this.initializeRequest();
    try {
      if (this.ectData.initialized) {
        store.flowStatus = await ApiCall.instance.GetFlowState();
      }
    } catch (e) {}
  }

  async initializeRequest() {
    if (this.initialised) {
      return;
    }
    this.initialised = true;

    if (store.device.browser.name == Browser.Mi) {
      this.apiErrorEmitter('Mi Browser nu este acceptat. Va rugam utilizati Chrome.', 'Request Initialisation RequestId:' + store.requestId + ' RedirectId:' + store.redirectId);
      return;
    }

    try {
      await ApiCall.instance.AddLog({ phase: 'debug mode' }, this.logInit);
    } catch {}

    try {
      if (!store.device.device.isMobile && store.redirectId == '' && store.environment != 'DEMO') {
        store.redirectId = getUUID();
        this.redirect_id = store.redirectId;
      }
      if (store.token != '' && (store.requestId != '' || store.redirectId != '') && !store.initialized) {
        await ApiCall.instance.AddIdentificationRequest(packageJson.version);
        store.debug ? LoggerService.enable() : LoggerService.disable();
        await this.treatMobileRedirect();

        this.ectData.initialized = true;
        this.ectData.flowStatus = store.flowStatus;
        this.ectData.idModelsPath = store.idModelsPath;
        this.ectData.faceModelsPath = store.faceModelsPath;
        await db.ectdata.put(this.ectData);
        modelLoadingService.init();
      }
    } catch (e) {
      this.appInsights.trackException({ name: 'IdentificationComponent | error', requestId: store.requestId }, e);
      this.apiErrorEmitter(e, 'Request Initialisation RequestId:' + store.requestId + ' RedirectId:' + store.redirectId);
    }
  }

  private async treatMobileRedirect() {
    if (this.order_id && this.order_id != '') {
      return;
    }
    this.order_id = store.requestId;
    this.ectData = await db.ectdata.get(store.requestId);
    if (!this.ectData) {
      await db.ectdata.add({
        orderId: store.requestId,
        redirectId: store.redirectId,
        timestamp: new Date(),
        initialized: true,
        phoneNumber: store.phoneNumber,
        refreshStarted: false,
        flowStatus: store.flowStatus,
        idModelsPath: store.idModelsPath,
        faceModelsPath: store.faceModelsPath,
        citizenship: store.citizenship,
        finished: false,
      });
      this.ectData = await db.ectdata.get(store.requestId);
    }
  }

  disconnectedCallback() {
    Events.flowEvent(FlowSteps.ComponentLoaded, FlowMoments.Finalized);
  }

  render() {
    let currentBlock = (
      <div class="loader-overlay">
        <div class="loader-spinner"></div>
      </div>
    );

    if (store.flowStatus == FlowStatus.CITIZENSHIP) {
      currentBlock = <citizenship-selection></citizenship-selection>;
    } else if (store.flowStatus == FlowStatus.LANDING) {
      currentBlock = <landing-validation></landing-validation>;
    } else if (store.flowStatus == FlowStatus.MOBILE) {
      currentBlock = <mobile-redirect></mobile-redirect>;
    } else if (store.flowStatus == FlowStatus.AGREEMENT) {
      currentBlock = <agreement-info></agreement-info>;
    } else if (store.flowStatus == FlowStatus.PHONE) {
      currentBlock = <sms-send></sms-send>;
    } else if (store.flowStatus == FlowStatus.CODE) {
      currentBlock = <sms-code-validation></sms-code-validation>;
    } else if (store.flowStatus == FlowStatus.CODEERROR) {
      currentBlock = <sms-code-validation></sms-code-validation>;
    } else if (store.flowStatus == FlowStatus.IDTYPE) {
      currentBlock = <id-selection></id-selection>;
    } else if (store.flowStatus == FlowStatus.IDFRONTHOWTO) {
      LoggerService.log('identification-component | render | IDFRONTHOWTO');
      currentBlock = <how-to-info></how-to-info>;
    } else if (store.flowStatus == FlowStatus.IDFRONT) {
      LoggerService.log('identification-component | render | IDFRONT');
      currentBlock = <process-id id="processIdFront" mode="front"></process-id>;
    } else if (store.flowStatus == FlowStatus.IDBACKHOWTO) {
      LoggerService.log('identification-component | render | IDBACKHOWTO');
      currentBlock = <how-to-info></how-to-info>;
    } else if (store.flowStatus == FlowStatus.IDBACK) {
      LoggerService.log('identification-component | render | IDBACK');
      currentBlock = <process-id id="processIdBack" mode="back"></process-id>;
    } else if (store.flowStatus == FlowStatus.IDTILTHOWTO) {
      LoggerService.log('identification-component | render | IDTILTHOWTO');
      currentBlock = <how-to-info></how-to-info>;
    } else if (store.flowStatus == FlowStatus.IDTILT) {
      LoggerService.log('identification-component | render | TILT');
      currentBlock = <process-id id="processIdTilt" mode="tilt"></process-id>;
    } else if (store.flowStatus == FlowStatus.LIVENESSHOWTO) {
      LoggerService.log('identification-component | render | LIVENESSHOWTO');
      currentBlock = <how-to-info></how-to-info>;
    } else if (store.flowStatus == FlowStatus.LIVENESS) {
      LoggerService.log('identification-component | render | LIVENESS');
      currentBlock = <user-liveness id="userLiveness" mode="selfie"></user-liveness>;
    } else if (store.flowStatus == FlowStatus.LIVENESSGESTUREHOWTO) {
      LoggerService.log('identification-component | render | LIVENESSGESTUREHOWTO');
      currentBlock = <state-transition></state-transition>;
    } else if (store.flowStatus == FlowStatus.LIVENESSGESTURE) {
      LoggerService.log('identification-component | render | LIVENESSGESTURE');
      currentBlock = <user-liveness id="userLivenessGesture" mode="selfieGesture"></user-liveness>;
    } else if (store.flowStatus == FlowStatus.COMPLETE) {
      LoggerService.log('identification-component | render | COMPLETE');
      currentBlock = <end-redirect></end-redirect>;
    } else if (store.flowStatus == FlowStatus.ERROREND) {
      currentBlock = <error-end errorTitle={this.errorTitle} message={this.errorMessage}></error-end>;
    } else if (store.flowStatus == FlowStatus.CAMERAERROR) {
      currentBlock = <camera-error></camera-error>;
    } else if (store.flowStatus == FlowStatus.RANDOM) {
      LoggerService.log('identification-component | render | RANDOM');
      currentBlock = <random-action></random-action>;
    }

    return <div class="full">{currentBlock}</div>;
  }
}
